const collator = new Intl.Collator(undefined, { sensitivity: "base" });

/**
 * Check if a substring is contained in a string using basic fuzzy matching.
 *
 * It performs a case-insensitive, diacritic-insensitive, and locale-aware search.
 *
 * At least two characters must match consecutively for each token, except
 * possibly for the last token, which can be one character long (to
 * accommodate typing), and except if the matched token is a single character.
 *
 * @example insensitiveStringContains("Zéphyr véloce", "zephyr") // true
 * @example insensitiveStringContains("Zephyr veloce", "ZÉPHYR") // true
 * @example insensitiveStringContains("fuzzy", "fu") // true
 * @example insensitiveStringContains("fouzzy", "fu") // false
 * @example insensitiveStringContains("tableau des couts du client", "ta cou c") // true
 * @example insensitiveStringContains("tableau des couts du client", "ta cou cl") // true
 *
 * @param {string} string String in which to search.
 * @param {string} substring Substring to find.
 * @returns `true` if `string` contains `substring` based on fuzzy matching rules, `false` otherwise.
 */
export function insensitiveStringContains(string, substring) {
  if (string === "" && substring === "") {
    return true;
  }

  if (!string || !substring || substring.length > string.length) {
    return false;
  }

  const tokens = substring.trim().split(/\s+/);
  if (tokens.length === 1 && tokens[0].length === 1) {
    return findTokenInString(string, tokens[0], 0, 1) !== -1;
  }

  let currentIndex = 0;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const isLastToken = i === tokens.length - 1;
    const minConsecutiveChars = isLastToken || token.length === 1 ? 1 : 2;

    const matchIndex = findTokenInString(string, token, currentIndex, minConsecutiveChars);
    if (matchIndex === -1) {
      return false;
    }
    currentIndex = matchIndex + token.length;
  }

  return true;
}

function findTokenInString(string, token, startIndex, minConsecutiveChars) {
  for (let i = startIndex; i <= string.length - token.length; i++) {
    let consecutiveChars = 0;
    let match = true;

    for (let j = 0; j < token.length; j++) {
      if (collator.compare(string[i + j], token[j]) === 0) {
        consecutiveChars++;
      } else {
        if (consecutiveChars >= minConsecutiveChars) {
          match = false;
          break;
        } else {
          match = false;
          break;
        }
      }
    }

    if (match && consecutiveChars >= minConsecutiveChars) {
      return i;
    }
  }
  return -1;
}

export default (Alpine) => {
  Alpine.magic("insensitiveStringContains", () => insensitiveStringContains);
};
